import { useSnackbar } from "notistack";
import {
  useBatchProcessInvoicesMutation,
  useConsolidateInvoicesMutation,
  useGetInvoiceListQuery,
  useSendInvoiceMutation,
  useUpdateInvoiceStatusMutation,
} from "../../../graphql/generated";
import useDialog from "../../../utils/hooks/useDialog";
import InvoiceList from "./InvoiceList";
import { useTranslation } from "react-i18next";
import { useState } from "react";

const InvoiceListContainer = () => {
  const { t } = useTranslation(["finance", "common"]);
  let refetch = () => {};
  // I need a handle on refetch so I can call it
  // a new settlement has been generated
  const useGetInvoiceListQueryCustom: typeof useGetInvoiceListQuery = (
    variables,
    options
  ) => {
    const query = useGetInvoiceListQuery(variables, {
      ...options,
    });
    refetch = query.refetch;
    return query;
  };
  useGetInvoiceListQueryCustom.fetcher = useGetInvoiceListQuery.fetcher;
  useGetInvoiceListQueryCustom.getKey = useGetInvoiceListQuery.getKey;
  const [batchProcessModalOpen, setBatchProcessModalOpen] = useState(false);
  const sendInvoiceMutation = useSendInvoiceMutation();
  const updateInvoiceStatusMutation = useUpdateInvoiceStatusMutation();
  const consolidateInvoicesMutation = useConsolidateInvoicesMutation();
  const batchProcessInvoices = useBatchProcessInvoicesMutation();
  const [isLoadingRequest, setIsLoadingRequest] = useState(false);

  const { enqueueSnackbar } = useSnackbar();
  const { showDialog } = useDialog();
  return (
    <InvoiceList
      queryHook={useGetInvoiceListQueryCustom}
      onSendInvoice={async (invoice) => {
        try {
          await sendInvoiceMutation.mutateAsync({
            invoiceId: invoice._id,
          });
          enqueueSnackbar(t("sendInvoiceSuccess", "Invoice successfully sent"));
          refetch();
        } catch (e) {
          console.error(e);
          showDialog({
            type: "error",
            title: t("error.sendInvoice", "Error sending invoice"),
            description:
              (e as Error).message ||
              t("common:error.unknownError", "Unknown error"),
          });
        }
      }}
      onInvoiceStatusUpdate={async (invoice, status, closingNote) => {
        try {
          await updateInvoiceStatusMutation.mutateAsync({
            invoiceId: invoice._id,
            status,
            closingNote,
          });
          enqueueSnackbar(
            t(
              "updateInvoiceStatusSuccess",
              "Invoice status successfully updated"
            )
          );
          refetch();
        } catch (e) {
          console.error(e);
          showDialog({
            type: "error",
            title: t(
              "error.updateInvoiceStatusError",
              "Error updating invoice status"
            ),
            description:
              (e as Error).message ||
              t("common:error.unknownError", "Unknown error"),
          });
        }
      }}
      onInvoicesConsolidate={async (invoices) => {
        try {
          await consolidateInvoicesMutation.mutateAsync({
            invoiceIds: invoices.map((invoice) => invoice._id),
          });
          enqueueSnackbar(
            t(
              "consolidateInvoicesSuccess",
              "Invoices successfully consolidated"
            )
          );
          refetch();
        } catch (e) {
          console.error(e);
          showDialog({
            type: "error",
            title: t(
              "error.consolidateInvoicesError",
              "Error consolidating invoices"
            ),
            description:
              (e as Error).message ||
              t("common:error.unknownError", "Unknown error"),
          });
        }
      }}
      batchProcessModalOpen={batchProcessModalOpen}
      isLoadingRequest={isLoadingRequest}
      setBatchProcessModalOpen={setBatchProcessModalOpen}
      onConfirmBatchProcess={async (invoices, statusBatchProcess) => {
        try {
          setIsLoadingRequest(true);
          const result = await batchProcessInvoices.mutateAsync({
            invoiceIds: invoices.map((invoice) => invoice._id),
            status: statusBatchProcess,
          });
          setIsLoadingRequest(false);
          if (
            result.batchProcessInvoices?.textError ||
            result.batchProcessInvoices?.textWarning
          ) {
            result.batchProcessInvoices?.textError &&
              showDialog({
                type: "error",
                title: t("common:error.title"),
                description: result.batchProcessInvoices.textError,
              });
            result.batchProcessInvoices?.textWarning &&
              showDialog({
                type: "secondary",
                title: t("common:warning"),
                description: result.batchProcessInvoices.textWarning,
              });
            if (!result.batchProcessInvoices?.textError) {
              refetch();
              setBatchProcessModalOpen(false);
            }
            return;
          }
          refetch();
          setBatchProcessModalOpen(false);
        } catch (error) {
          setIsLoadingRequest(false);
          showDialog({
            type: "error",
            title: t("common:error.title"),
            description: (error as Error).message,
          });
        }
      }}
    />
  );
};

export default InvoiceListContainer;
